const STORAGE_KEY = "hockeyly_onboarding";

export type OnboardingStep =
    | "INTRO"
    | "NAVIGATION"
    | "DASHBOARD_OVERVIEW"
    | "DASHBOARD_ANALYTICS"
    | "DASHBOARD_HEATMAP"
    | "DASHBOARD_COMPARE"
    | "DASHBOARD_TEAM_COMPARISON"
    | "TOURNAMENT"
    | "TOURNAMENT_COMPARE"
    | "TOURNAMENT_RANKING"
    | "TEAMS"
    | "TEAM_PROFILE"
    | "TEAM_PLAYERS"
    | "TEAM_ANALYSIS"
    | "TEAM_COMPARE"
    | "TEAM_H2H"
    | "PLAYERS"
    | "PLAYER_PROFILE"
    | "LEADERBOARDS"
    | "SCOUTING_PLAYERS"
    | "KPI_CURRENT"
    | "CHAT"
    | "SETTINGS_MULTIPLIER"
    | "SETTINGS_REPORT"
    | "DASHBOARD_PERIOD_DATA"
    | "TOURNAMENT_LINES";

export function getCompletedSteps(): OnboardingStep[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

export function isStepComplete(step: OnboardingStep): boolean {
    return getCompletedSteps().includes(step);
}

export function markStepComplete(step: OnboardingStep): void {
    const steps = getCompletedSteps();
    if (!steps.includes(step)) {
        steps.push(step);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(steps));
    }
}

export const ONBOARDING_IDS = {
    NAV_DASHBOARD: "onboarding-nav-dashboard",
    NAV_PLAYERS: "onboarding-nav-players",
    NAV_TOURNAMENTS: "onboarding-nav-tournaments",
    NAV_LEADERBOARDS: "onboarding-nav-leaderboards",
    NAV_TEAMS: "onboarding-nav-teams",
    NAV_SCOUTING: "onboarding-nav-scouting",
    NAV_KPI: "onboarding-nav-kpi",
    GUIDE_ICON: "onboarding-guide-icon",
    NAV_CHAT: "onboarding-nav-chat",
    NAV_SETTINGS: "onboarding-nav-settings",
    DASHBOARD_HEADER: "onboarding-dashboard-header",
    DASHBOARD_AI_SUMMARY: "onboarding-dashboard-ai-summary",
    DASHBOARD_MATCH_SELECT: "onboarding-dashboard-match-select",
    DASHBOARD_NAV: "onboarding-dashboard-nav",
    DASHBOARD_LEADERBOARD: "onboarding-dashboard-leaderboard",
    DASHBOARD_BADGES: "onboarding-dashboard-badges",
    ANALYTICS_VIEW_MODE: "onboarding-analytics-view-mode",
    ANALYTICS_EXPORT: "onboarding-analytics-export",
    ANALYTICS_GOALKEEPER_TABLE: "onboarding-analytics-goalkeeper-table",
    ANALYTICS_PLAYER_TABLE: "onboarding-analytics-player-table",
    ANALYTICS_DATA_AREA: "onboarding-analytics-data-area",
    HEATMAP_RESULT_FILTERS: "onboarding-heatmap-result-filters",
    HEATMAP_FILTERS: "onboarding-heatmap-filters",
    HEATMAP_CHART: "onboarding-heatmap-chart",
    COMPARE_ENGINE: "onboarding-compare-engine",
    COMPARE_PERFORMANCE: "onboarding-compare-performance",
    TEAM_KPI: "onboarding-team-kpi",
    TEAM_PLAYERS: "onboarding-team-players",
    TEAM_GOALKEEPERS: "onboarding-team-goalkeepers",
    LEADERBOARDS_TABLE: "onboarding-leaderboards-table",
    LEADERBOARDS_NAV: "onboarding-leaderboards-nav",
    LEADERBOARDS_METRIC: "onboarding-leaderboards-metric",
    LEADERBOARDS_CONTEXT_SELECTOR: "onboarding-leaderboards-context-selector",
    TOURNAMENT_HEADER: "onboarding-tournament-header",
    TOURNAMENT_SELECTOR: "onboarding-tournament-selector",
    TOURNAMENT_EXPORT: "onboarding-tournament-export",
    TOURNAMENT_NAV: "onboarding-tournament-nav",
    TOURNAMENT_VIEW_MODE: "onboarding-tournament-view-mode",
    TOURNAMENT_COMPARE_EXPORT: "onboarding-tournament-compare-export",
    TOURNAMENT_STAT_CATEGORY: "onboarding-tournament-stat-category",
    TOURNAMENT_METRICS: "onboarding-tournament-metrics",
    TOURNAMENT_RANKING_TABLE: "onboarding-tournament-ranking-table",
    COMPARE_PLAYER_SELECT: "onboarding-compare-player-select",
    COMPARE_STATS_TABLE: "onboarding-compare-stats-table",
    COMPARE_STATS_TABLE_MOBILE: "onboarding-compare-stats-table-mobile",
    COMPARE_RADAR: "onboarding-compare-radar",
    TOURNAMENT_PERFORMANCE: "onboarding-tournament-performance",
    TOURNAMENT_HOME_AWAY: "onboarding-tournament-home-away",
    TOURNAMENT_GAMES_SUMMARY: "onboarding-tournament-games-summary",
    TOURNAMENT_CARDS: "onboarding-tournament-cards",
    TOURNAMENT_TOP_PERFORMERS: "onboarding-tournament-top-performers",
    TOURNAMENT_RECENT_GAMES: "onboarding-tournament-recent-games",
    TOURNAMENT_QUICK_STATS: "onboarding-tournament-quick-stats",
    TOURNAMENT_CHART: "onboarding-tournament-chart",
    TOURNAMENT_CHART_TABS: "onboarding-tournament-chart-tabs",
    TOURNAMENT_ACHIEVEMENTS: "onboarding-tournament-achievements",
    PLAYERS_FILTERS: "onboarding-players-filters",
    PLAYERS_SORT: "onboarding-players-sort",
    PLAYERS_GOALKEEPERS: "onboarding-players-goalkeepers",
    PLAYERS_FIELD: "onboarding-players-field",
    PLAYER_HEADER: "onboarding-player-header",
    PLAYER_HERO: "onboarding-player-hero",
    PLAYER_SKILLS: "onboarding-player-skills",
    PLAYER_RINK_MAP: "onboarding-player-rink-map",
    PLAYER_KPI_SELECTOR: "onboarding-player-kpi-selector",
    PLAYER_KPI_CHART: "onboarding-player-kpi-chart",
    PLAYER_KPI_DETAILS: "onboarding-player-kpi-details",
    PLAYER_ACHIEVEMENTS: "onboarding-player-achievements",
    TEAMS_SELECTOR: "onboarding-teams-selector",
    TEAMS_SORT: "onboarding-teams-sort",
    TEAMS_GRID: "onboarding-teams-grid",
    TEAM_PROFILE_HEADER: "onboarding-team-profile-header",
    TEAM_PROFILE_NAV: "onboarding-team-profile-nav",
    TEAM_PROFILE_METRICS: "onboarding-team-profile-metrics",
    TEAM_PROFILE_GAMES_SUMMARY: "onboarding-team-profile-games-summary",
    TEAM_PROFILE_TOP_PERFORMERS: "onboarding-team-profile-top-performers",
    TEAM_PROFILE_RECENT_GAMES: "onboarding-team-profile-recent-games",
    TEAM_PROFILE_HOME_AWAY: "onboarding-team-profile-home-away",
    TEAM_PLAYERS_LIST: "onboarding-team-players-list",
    TEAM_ANALYSIS_GOALKEEPERS: "onboarding-team-analysis-goalkeepers",
    TEAM_ANALYSIS_PLAYERS: "onboarding-team-analysis-players",
    TEAM_H2H_MATCH_SELECTOR: "onboarding-team-h2h-match-selector",
    TEAM_H2H_LINES: "onboarding-team-h2h-lines",
    TEAM_H2H_CHARTS: "onboarding-team-h2h-charts",
    SCOUTING_TOURNAMENT: "onboarding-scouting-tournament",
    SCOUTING_NAV: "onboarding-scouting-nav",
    SCOUTING_PLAYERS: "onboarding-scouting-players",
    SCOUTING_PLAYERS_TABLE: "onboarding-scouting-players-table",
    SCOUTING_SEARCH: "onboarding-scouting-search",
    SCOUTING_POSITION_FILTER: "onboarding-scouting-position-filter",
    SCOUTING_TEAM_FILTER: "onboarding-scouting-team-filter",
    SCOUTING_STAT_CATEGORY: "onboarding-scouting-stat-category",
    SCOUTING_ROLE_COL: "onboarding-scouting-role-col",
    KPIS_NAV: "onboarding-kpis-nav",
    KPIS_CURRENT_INFO: "onboarding-kpis-current-info",
    KPIS_METHODOLOGY: "onboarding-kpis-methodology",
    CHAT: "onboarding-chat",
    CHAT_SIDEBAR: "onboarding-chat-sidebar",
    CHAT_MAIN: "onboarding-chat-main",
    CHAT_INPUT: "onboarding-chat-input",
    SETTINGS_NAV: "onboarding-settings-nav",
    SETTINGS_HEADER: "onboarding-settings-header",
    SETTINGS_HEADER_BUTTONS: "onboarding-settings-header-buttons",
    SETTINGS_LIST: "onboarding-settings-list",
    SETTINGS_RESET_ALL: "onboarding-settings-reset-all",
    SETTINGS_REPORT_HEADER: "onboarding-settings-report-header",
    SETTINGS_REPORT_SAVE: "onboarding-settings-report-save",
    SETTINGS_REPORT_LIST: "onboarding-settings-report-list",
    PERIOD_DATA_NAV: "onboarding-period-data-nav",
    PERIOD_DATA_CONTENT: "onboarding-period-data-content",
    TOURNAMENT_LINES_OPPONENT_SELECT: "onboarding-tournament-lines-opponent-select",
    NAV_TEAM_SELECTOR: "onboarding-nav-team-selector",
    FOOTER_NAV: "onboarding-footer-nav",
    NAV_DASHBOARD_MOBILE: "onboarding-nav-dashboard-mobile",
    NAV_PLAYERS_MOBILE: "onboarding-nav-players-mobile",
    NAV_TOURNAMENTS_MOBILE: "onboarding-nav-tournaments-mobile",
    NAV_LEADERBOARDS_MOBILE: "onboarding-nav-leaderboards-mobile",
    NAV_SCOUTING_MOBILE: "onboarding-nav-scouting-mobile",
    NAV_KPI_MOBILE: "onboarding-nav-kpi-mobile",
    NAV_CHAT_MOBILE: "onboarding-nav-chat-mobile",
    NAV_SETTINGS_MOBILE: "onboarding-nav-settings-mobile",
    GUIDE_ICON_MOBILE: "onboarding-guide-icon-mobile",
    NAV_FOOTER_MOBILE: "onboarding-nav-footer-mobile",
} as const;
